import classNames from 'classnames';
import { BasicProps } from 'components/types/BasicProps';
import { INft } from 'logic/types/nft/INft';
import { FC, useEffect, useMemo, useState } from 'react';
import { accountLiterals } from './literals';
import { Tokens } from './tokens-section';
import { NftsSection } from './nfts-section';
import { useCustomParams } from 'logic/hooks';
import { useUpdateEffect } from 'react-use';
import { FilterOption } from 'components/filter/Filter';

type Props = BasicProps & {
  nfts: INft[];
  selectedNfts: INft[];
  onNftsWithdrawn: (nfts: INft[]) => void;
  onSelectedNft: (nft: INft) => void;
  isLoadingNfts: boolean;
  filterOptions: FilterOption[];
  areNfts?: boolean;
};

enum AssetType {
  TOKENS = 'tokens',
  NFTS = 'nfts',
}

const { nftsLabel, selectMultipleCta, title, tokensLabel } =
  accountLiterals.myAvailableAssets;

export const MyAvailableAssets: FC<Props> = (props) => {
  const {
    nfts,
    className,
    filterOptions,
    onNftsWithdrawn,
    selectedNfts,
    onSelectedNft,
    isLoadingNfts,
    areNfts,
  } = props;
  const {
    setOrReplaceParamValue,
    getParamValue,
    searchParams,
    toggleBooleanParam,
  } = useCustomParams();

  const [multipleSelectionActive, setMultipleSelection] =
    useState<boolean>(false);
  const [assetTypeSelected, setAssetTypeSelected] = useState<AssetType>();

  useEffect(() => {
    const multipleSelection = searchParams.get('multipleSelection');

    if (multipleSelection === 'true') {
      setMultipleSelection(true);
    } else if (multipleSelection === 'false') {
      setMultipleSelection(false);
    } else {
      setOrReplaceParamValue('multipleSelection', 'false');
    }

    const assetType = getParamValue('asset');

    if (assetType === AssetType.TOKENS || assetType === AssetType.NFTS) {
      setAssetTypeSelected(assetType);
    } else {
      setOrReplaceParamValue('asset', AssetType.NFTS);
    }
  }, [searchParams]);

  const divStyles = useMemo<string>(
    () => (assetTypeSelected === AssetType.TOKENS ? 'mx-[15px]' : ''),
    [assetTypeSelected]
  );

  const selectMultipleStyles = useMemo<string>(
    () => (multipleSelectionActive ? 'bg-uyellow text-dark' : 'text-uyellow'),
    [multipleSelectionActive]
  );

  const assetSelectedStyles = 'font-bold border-uyellow';
  const assetNotSelectedStyles = 'font-medium text-white border-transparent';

  useUpdateEffect(() => {
    if (
      assetTypeSelected !== AssetType.TOKENS &&
      assetTypeSelected !== AssetType.NFTS
    ) {
      handleAssetTypeSelection(AssetType.NFTS);
    }
  }, [assetTypeSelected]);

  const toggleMultipleSelection = () => {
    toggleBooleanParam('multipleSelection');
  };

  const handleAssetTypeSelection = (assetType: AssetType) => {
    setOrReplaceParamValue('asset', assetType);
  };

  return (
    <section
      className={classNames(
        'p-0.5 border-unlockd rounded-[20px]',
        className,
        multipleSelectionActive && 'mb-[100px]'
      )}
    >
      <div className="w-full rounded-[20px] bg-secondary pt-[60px] px-[45px] pb-2 flex flex-col">
        <div
          className={classNames(
            'w-full flex flex-col gap-[30px] border-b-[1px] border-white',
            divStyles
          )}
        >
          <div className="w-full flex justify-between">
            <h2 className="text-2xl font-bold">{title}</h2>
            {assetTypeSelected === AssetType.NFTS && nfts.length !== 0 && (
              <button
                className={classNames(
                  'border border-uyellow rounded-[4px] px-3 py-0.5 text-xl',
                  selectMultipleStyles
                )}
                type="button"
                onClick={toggleMultipleSelection}
              >
                {selectMultipleCta}
              </button>
            )}
          </div>
          <div className="ml-[60px] flex gap-[60px]">
            <button
              className={classNames(
                'border-b-[3px] pb-[11px]',
                assetTypeSelected === AssetType.NFTS
                  ? assetSelectedStyles
                  : assetNotSelectedStyles
              )}
              type="button"
              onClick={() => handleAssetTypeSelection(AssetType.NFTS)}
            >
              {nftsLabel}
            </button>
            <button
              className={classNames(
                'border-b-[3px] pb-[11px]',
                assetTypeSelected === AssetType.TOKENS
                  ? assetSelectedStyles
                  : assetNotSelectedStyles
              )}
              type="button"
              onClick={() => handleAssetTypeSelection(AssetType.TOKENS)}
            >
              {tokensLabel}
            </button>
          </div>
        </div>
        {assetTypeSelected === AssetType.TOKENS && (
          <Tokens className="mx-[15px] mt-[30px] mb-[52px]" />
        )}
        {assetTypeSelected === AssetType.NFTS && (
          <NftsSection
            className="h-[800px] mt-[30px]"
            nfts={nfts}
            isLoadingNfts={isLoadingNfts}
            selectedNfts={selectedNfts}
            multipleSelectionActive={multipleSelectionActive}
            onNftsWithdrawn={onNftsWithdrawn}
            onSelectedNft={onSelectedNft}
            filterOptions={filterOptions}
            areNfts={areNfts}
          />
        )}
      </div>
    </section>
  );
};
